import { useI18n } from "react-simple-i18n";
import { Box, Typography, Grid, Button, Rating, Divider, List, ListItem, ListItemIcon, ListItemText, IconButton } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { Layout } from "../../components/layout/layout";
import { GlobalFirstSection, GlobalSpacing } from "../../components/layout/styles";
import { mockProducts } from "./mock.data";
import { useGlobalContext } from "../../provider";

const ProductDetails = () => {
    const { t } = useI18n();
    const { id } = useParams();
    const navigate = useNavigate();
    const [globalState]: GlobalContextType = useGlobalContext();

    const product = mockProducts.find(p => p.id === id);
    const [selectedImage, setSelectedImage] = useState(product?.images[0]);
    const [isZoomed, setIsZoomed] = useState(false);
    const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const imageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Add a small delay to trigger the entrance animation
        const timer = setTimeout(() => {
            setIsPageLoaded(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    const handleThumbnailClick = (image: string) => {
        setSelectedImage(image);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!imageRef.current) return;

        const rect = imageRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        setZoomPosition({ x, y });
    };

    if (!product) {
        return (
            <Layout>
                <GlobalFirstSection sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: 'center', py: 8 }}>
                    <Typography variant="h4">{t("product.productNotFound")}</Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/product')}
                        sx={{ mt: 2 }}
                    >
                        {t("product.returnToProducts")}
                    </Button>
                </GlobalFirstSection>
            </Layout>
        );
    }

    return (
        <Layout>
            <Box
                sx={{
                    py: 8,
                    opacity: isPageLoaded ? 1 : 0,
                    transform: isPageLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            >
                <GlobalSpacing>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/product')}
                        sx={{
                            mb: 4,
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                transform: 'translateX(-4px)',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    >
                        {t("product.returnToProducts")}
                    </Button>

                    <Grid container spacing={6}>
                        <Grid item xs={12} md={6}>
                            <Box>
                                <Box
                                    ref={imageRef}
                                    onMouseEnter={() => setIsZoomed(true)}
                                    onMouseLeave={() => setIsZoomed(false)}
                                    onMouseMove={handleMouseMove}
                                    sx={{
                                        position: 'relative',
                                        width: '100%',
                                        height: '400px',
                                        borderRadius: '8px',
                                        mb: 2,
                                        overflow: 'hidden',
                                        cursor: 'zoom-in',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                                        }
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={selectedImage}
                                        alt={product.title}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                            transform: isZoomed ? 'scale(1.05)' : 'scale(1)',
                                        }}
                                    />
                                    {isZoomed && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                backgroundImage: `url(${selectedImage})`,
                                                backgroundSize: '300%',
                                                backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                                                backgroundRepeat: 'no-repeat',
                                                pointerEvents: 'none',
                                                zIndex: 1,
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                borderRadius: '8px',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                                opacity: 0,
                                                animation: 'fadeIn 0.3s ease-in-out forwards',
                                                '@keyframes fadeIn': {
                                                    '0%': { opacity: 0 },
                                                    '100%': { opacity: 1 }
                                                }
                                            }}
                                        />
                                    )}
                                </Box>
                                <Grid container spacing={1}>
                                    {product.images.map((image, index) => (
                                        <Grid item xs={3} key={index}>
                                            <Box
                                                component="img"
                                                src={image}
                                                alt={`${globalState.lang === "en" ? product.title : product.jpTitle} ${index + 1}`}
                                                onClick={() => handleThumbnailClick(image)}
                                                sx={{
                                                    width: '100%',
                                                    height: '80px',
                                                    objectFit: 'cover',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    border: selectedImage === image ? '2px solid' : 'none',
                                                    borderColor: 'primary.main',
                                                    opacity: selectedImage === image ? 1 : 0.7,
                                                    transform: selectedImage === image ? 'scale(1.05)' : 'scale(1)',
                                                    '&:hover': {
                                                        transform: 'scale(1.05)',
                                                        opacity: 1,
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                opacity: isPageLoaded ? 1 : 0,
                                transform: isPageLoaded ? 'translateX(0)' : 'translateX(20px)',
                                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s'
                            }}>
                                <Typography variant="h4" gutterBottom>
                                    {globalState.lang === "en" ? product.title : product.jpTitle}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Rating value={product.rating} precision={0.5} readOnly />
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                        {t("product.reviews", product.reviews.toString())}
                                    </Typography>
                                </Box>

                                <Typography variant="h5" color="primary" gutterBottom>
                                    ${product.price}
                                </Typography>

                                <Typography variant="body1" paragraph>
                                    {globalState.lang === "en" ? product.description : product.jpDescription}
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<ShoppingCartIcon />}
                                        fullWidth
                                        sx={{
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                            }
                                        }}
                                    >
                                        {t("buttons.addToCard")}
                                    </Button>
                                    <IconButton
                                        color="primary"
                                        size="large"
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'primary.main',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'scale(1.1)',
                                                backgroundColor: 'primary.main',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        <FavoriteIcon />
                                    </IconButton>
                                    <IconButton
                                        color="primary"
                                        size="large"
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'primary.main',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'scale(1.1)',
                                                backgroundColor: 'primary.main',
                                                color: 'white'
                                            }
                                        }}
                                    >
                                        <ShareIcon />
                                    </IconButton>
                                </Box>

                                <Divider sx={{ my: 4 }} />

                                <Typography variant="h6" gutterBottom>
                                    {t("product.features")}
                                </Typography>
                                <List>
                                    {(globalState.lang === "en" ? product.features : product.jpFeatures).map((feature, index) => (
                                        <ListItem key={index}>
                                            <ListItemIcon>
                                                <CheckCircleIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText primary={feature} />
                                        </ListItem>
                                    ))}
                                </List>

                                <Box sx={{ mt: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("product.stockStatus", product.stock > 0 ? "In Stock" : "Out of Stock")}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("product.category", product.category)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </GlobalSpacing>
            </Box>
        </Layout>
    );
};

export { ProductDetails };